import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  TemplateRef,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-tab',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './tab.component.html',
  styleUrl: './tab.component.scss',
})
export class TabComponent {
  @Input() tabs: any[] = [];
  @Input() selectedIndex = 0;
  @Input() hasValues: boolean[] = [];
  @Input() vScroll = true;
  @Input() hScroll = false;
  @Input() marginTop = false;
  @ContentChild('tabLabel') tabLabel!: TemplateRef<any>;
  @Input() scrollOffset?: number;
  @Output() tabSelected = new EventEmitter<number>();

  tabId: string = '';
  container!: HTMLElement | null;
  isBrowser: any;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (this.isBrowser && this.vScroll) this.checkScrollPosition();
  }

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.generateUniqueId();
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.container = document.getElementById(this.tabId);
    }
  }

  selectTab(index: number): void {
  this.selectedIndex = index;
  this.tabSelected.emit(index);
  const target = this.tabs?.[index]?.target;
  if (this.isBrowser && this.vScroll && target) {
    const selector = target.startsWith('#') ? target : `#${target}`;
    const targetElement = document.querySelector(selector);

    if (targetElement) {
      const targetPosition =
        targetElement.getBoundingClientRect().top +
        window.scrollY -
        (this.scrollOffset ?? 100);

      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    }
  }

  if (this.isBrowser && this.hScroll) {
    const tabElement = this.container?.querySelectorAll('.nav-item')[index];
    tabElement?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }
}

  private checkScrollPosition(): void {
    if (!this.isBrowser || !this.tabs.length) return;

    const offsets: (number | null)[] = this.tabs.map((tab) => {
      const element = document.querySelector(tab.target);
      if (element) {
        return (
          element.getBoundingClientRect().top +
          window.scrollY -
          (this.scrollOffset ?? 100)
        );
      }
      return null; // explicitly return null if not found
    });

    const scrollPosition = window.scrollY + (this.scrollOffset ?? 100);

    let activeIndex = offsets.findIndex((offset, index) => {
      return (
        offset !== null && // ✅ guard for null
        scrollPosition >= offset &&
        (index === this.tabs.length - 1 ||
          (offsets[index + 1] !== null &&
            scrollPosition < (offsets[index + 1] as number)))
      );
    });

    // ✅ Only update if something matched
    if (activeIndex !== -1 && activeIndex !== this.selectedIndex) {
      this.selectedIndex = activeIndex;
      this.tabSelected.emit(this.selectedIndex);
    }
  }

  generateUniqueId() {
    this.tabId = `txl-tab-${new Date().getTime()}-${Math.floor(
      Math.random() * 1000
    )}`;
  }
}
